import { computed, onBeforeUnmount, onMounted, ref, type ComputedRef, type Ref } from 'vue'
import { useRuntimeConfig, useState } from '#imports'
import { useOrgStore } from '~~/stores/org'
import { DEV_MAINTENANCE_FORCED_STORAGE_KEY } from '~~/utils/devLabCrossFrame'

type TMaintenanceComponent = 'api' | 'ihm'

type MaintenanceComponentState = {
  component: TMaintenanceComponent
  active: boolean
  startedAt: string | null
  expiresAt: string | null
  requestedByNni: string | null
  estimatedRemainingSeconds: number | null
}

export type DeploymentMaintenanceState = {
  active: boolean
  components: MaintenanceComponentState[]
  deployEnv: string | null
}

const DEFAULT_STATE: DeploymentMaintenanceState = {
  active: false,
  components: [],
  deployEnv: null,
}

const MUTATION_BLOCKED_MESSAGE =
  "Mise à jour temporairement indisponible pendant la maintenance, pour éviter la perte de données non enregistrées qui pourrait ne pas être prise en compte si l’application redémarre."

function readDevForcedStateFromStorage(): DeploymentMaintenanceState | null {
  if (!import.meta.client || !import.meta.dev) {
    return null
  }
  try {
    const raw = localStorage.getItem(DEV_MAINTENANCE_FORCED_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as DeploymentMaintenanceState
    if (!parsed || typeof parsed.active !== 'boolean' || !Array.isArray(parsed.components)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeDevForcedStateToStorage(next: DeploymentMaintenanceState | null): void {
  if (!import.meta.client || !import.meta.dev) {
    return
  }
  if (next === null) {
    localStorage.removeItem(DEV_MAINTENANCE_FORCED_STORAGE_KEY)
    return
  }
  localStorage.setItem(DEV_MAINTENANCE_FORCED_STORAGE_KEY, JSON.stringify(next))
}

function isMutatingMethod(method: string): boolean {
  return method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE'
}

function parseIsoToMs(value: string | null): number | null {
  if (!value) return null
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

function firstStartedAt(components: MaintenanceComponentState[]): string | null {
  return components
    .map((bucket) => bucket.startedAt)
    .find((value) => typeof value === 'string' && value.length > 0) ?? null
}

function mutationBlockedMessageFor(active: boolean): string {
  if (!active) {
    return 'Mise à jour indisponible.'
  }
  return MUTATION_BLOCKED_MESSAGE
}

function applyGraceEligibility(params: {
  maintenanceActive: boolean
  alreadyEligible: boolean
  startedAtMs: number | null
  editEnteredAtMs: number | null
  graceAvailable: Ref<boolean>
  graceConsumed: Ref<boolean>
}): void {
  if (!params.maintenanceActive) {
    params.graceAvailable.value = false
    params.graceConsumed.value = false
    return
  }

  if (params.alreadyEligible) return
  if (params.startedAtMs === null || params.editEnteredAtMs === null) return

  if (params.editEnteredAtMs < params.startedAtMs) {
    params.graceAvailable.value = true
    params.graceConsumed.value = false
  }
}

function applyModalTransition(params: {
  nowActive: boolean
  previousActive: boolean
  previousStartedAt: string | null
  currentStartedAt: string | null
  lastStartedAtSeen: Ref<string | null>
  hasSeenActiveThisTab: Ref<boolean>
  shouldShowUpdatedModal: Ref<boolean>
}): void {
  if (params.nowActive) {
    params.hasSeenActiveThisTab.value = true
    params.lastStartedAtSeen.value = params.currentStartedAt
    return
  }

  const completedStartedAt = params.previousStartedAt ?? params.lastStartedAtSeen.value
  if (!params.previousActive || !params.hasSeenActiveThisTab.value || !completedStartedAt) return

  const storageKey = `monorga:maintenance:updated_shown:${completedStartedAt}`
  const alreadyShown = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey) === '1'
  if (alreadyShown) return

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(storageKey, '1')
  }
  params.shouldShowUpdatedModal.value = true
}

async function fetchRemoteMaintenanceState(baseURL: string): Promise<DeploymentMaintenanceState | undefined> {
  try {
    return await $fetch<DeploymentMaintenanceState>('/maintenance', { baseURL, method: 'GET' })
  } catch (error: unknown) {
    if (baseURL === '') {
      return await $fetch<DeploymentMaintenanceState>('/api/maintenance', { method: 'GET' })
    }
    if (import.meta.dev) {
      console.warn('[maintenance] refresh failed', error)
    }
    return undefined
  }
}

type MaintenancePoller = {
  start: () => void
  stop: () => void
}

function createMaintenancePoller(params: {
  isActive: () => boolean
  refresh: () => Promise<void>
  onDevForcedStorage: (event: StorageEvent) => void
  syncDevForcedFromStorage: () => void
}): MaintenancePoller {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void params.refresh()
    }
  }

  function nextPollDelayMs(): number {
    return params.isActive() ? 5_000 : 60_000
  }

  function scheduleNextPoll(): void {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    timeoutId = setTimeout(async () => {
      await params.refresh()
      scheduleNextPoll()
    }, nextPollDelayMs())
  }

  function start(): void {
    params.syncDevForcedFromStorage()
    if (import.meta.client && import.meta.dev) {
      window.addEventListener('storage', params.onDevForcedStorage)
    }
    void params.refresh()
    scheduleNextPoll()
    document.addEventListener('visibilitychange', onVisibilityChange)
  }

  function stop(): void {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = null
    document.removeEventListener('visibilitychange', onVisibilityChange)
    if (import.meta.client && import.meta.dev) {
      window.removeEventListener('storage', params.onDevForcedStorage)
    }
  }

  return { start, stop }
}

function selectDisplayedMaintenanceState(
  remote: DeploymentMaintenanceState,
  forced: DeploymentMaintenanceState | null,
): DeploymentMaintenanceState {
  if (import.meta.dev && forced !== null) {
    return forced
  }
  return remote
}

function earliestStartedAtMsFrom(components: MaintenanceComponentState[]): number | null {
  const startedAtValues = components
    .map((bucket) => parseIsoToMs(bucket.startedAt))
    .filter((value): value is number => value !== null)
  if (!startedAtValues.length) return null
  return Math.min(...startedAtValues)
}

function isWriteBlockedState(active: boolean, graceAvailable: boolean, graceConsumed: boolean): boolean {
  return active && !(graceAvailable && !graceConsumed)
}

function applyDevForcedState(
  slot: Ref<DeploymentMaintenanceState | null>,
  next: DeploymentMaintenanceState | null,
): void {
  if (!import.meta.dev) return
  slot.value = next
  writeDevForcedStateToStorage(next)
}

function readDevForcedIntoSlot(slot: Ref<DeploymentMaintenanceState | null>): void {
  if (!import.meta.dev) return
  slot.value = readDevForcedStateFromStorage()
}

function handleDevForcedStorageEvent(
  event: StorageEvent,
  slot: Ref<DeploymentMaintenanceState | null>,
): void {
  if (event.key !== DEV_MAINTENANCE_FORCED_STORAGE_KEY && event.key !== null) {
    return
  }
  readDevForcedIntoSlot(slot)
}

function consumeGraceWriteOnRuntime(
  available: Ref<boolean>,
  consumed: Ref<boolean>,
  maintenanceActive: boolean,
  forceExitEditMode: Ref<boolean>,
): void {
  if (!available.value || consumed.value) return
  consumed.value = true
  if (maintenanceActive) {
    forceExitEditMode.value = true
  }
}

function shouldBlockMutatingRequest(method: string, writeBlocked: boolean): boolean {
  return isMutatingMethod(method) && writeBlocked
}

type MaintenanceRefreshContext = {
  remoteState: Ref<DeploymentMaintenanceState>
  refreshInFlight: Ref<boolean>
  state: ComputedRef<DeploymentMaintenanceState>
  earliestStartedAtMs: ComputedRef<number | null>
  activeComponents: ComputedRef<MaintenanceComponentState[]>
  graceWriteAvailable: Ref<boolean>
  graceWriteConsumed: Ref<boolean>
  hasDevForcedOverride: ComputedRef<boolean>
  lastStartedAtSeen: Ref<string | null>
  hasSeenActiveThisTab: Ref<boolean>
  shouldShowUpdatedModal: Ref<boolean>
  editEnteredAtMs: number | null
  apiBaseUrl: string
}

function previousActiveForModal(ctx: MaintenanceRefreshContext, remoteWasActive: boolean): boolean {
  if (ctx.hasDevForcedOverride.value) {
    return ctx.state.value.active
  }
  return remoteWasActive
}

async function refreshDeploymentMaintenance(ctx: MaintenanceRefreshContext): Promise<void> {
  if (ctx.refreshInFlight.value) return
  ctx.refreshInFlight.value = true
  const previousActive = ctx.remoteState.value.active
  const previousStartedAt = ctx.lastStartedAtSeen.value
  try {
    const fetched = await fetchRemoteMaintenanceState(ctx.apiBaseUrl)
    if (fetched) ctx.remoteState.value = fetched
  } finally {
    ctx.refreshInFlight.value = false
  }

  applyGraceEligibility({
    maintenanceActive: ctx.state.value.active,
    alreadyEligible: ctx.graceWriteAvailable.value,
    startedAtMs: ctx.earliestStartedAtMs.value,
    editEnteredAtMs: ctx.editEnteredAtMs,
    graceAvailable: ctx.graceWriteAvailable,
    graceConsumed: ctx.graceWriteConsumed,
  })
  applyModalTransition({
    nowActive: ctx.state.value.active,
    previousActive: previousActiveForModal(ctx, previousActive),
    previousStartedAt,
    currentStartedAt: firstStartedAt(ctx.activeComponents.value),
    lastStartedAtSeen: ctx.lastStartedAtSeen,
    hasSeenActiveThisTab: ctx.hasSeenActiveThisTab,
    shouldShowUpdatedModal: ctx.shouldShowUpdatedModal,
  })

  if (!ctx.state.value.active) {
    ctx.graceWriteAvailable.value = false
    ctx.graceWriteConsumed.value = false
  }
}

function createDeploymentMaintenanceDerived(
  remoteState: Ref<DeploymentMaintenanceState>,
  devForcedState: Ref<DeploymentMaintenanceState | null>,
  graceWriteAvailable: Ref<boolean>,
  graceWriteConsumed: Ref<boolean>,
) {
  const hasDevForcedOverride = computed(() => import.meta.dev && devForcedState.value !== null)
  const state = computed(() => selectDisplayedMaintenanceState(remoteState.value, devForcedState.value))
  const activeComponents = computed(() => state.value.components.filter((bucket) => bucket.active))
  const earliestStartedAtMs = computed(() => earliestStartedAtMsFrom(activeComponents.value))
  const isWriteBlocked = computed(() =>
    isWriteBlockedState(state.value.active, graceWriteAvailable.value, graceWriteConsumed.value),
  )
  return { hasDevForcedOverride, state, activeComponents, earliestStartedAtMs, isWriteBlocked }
}

function bindDeploymentMaintenanceLifecycle(params: {
  autoRefresh: boolean
  refresh: () => Promise<void>
  isActive: () => boolean
  onDevForcedStorage: (event: StorageEvent) => void
  syncDevForcedFromStorage: () => void
}): void {
  if (params.autoRefresh) {
    const poller = createMaintenancePoller({
      isActive: params.isActive,
      refresh: params.refresh,
      onDevForcedStorage: params.onDevForcedStorage,
      syncDevForcedFromStorage: params.syncDevForcedFromStorage,
    })
    onMounted(poller.start)
    onBeforeUnmount(poller.stop)
    return
  }
  if (import.meta.client && import.meta.dev) {
    params.syncDevForcedFromStorage()
  }
}

/**
 * État de maintenance déploiement.
 * Le branchement vit dans des helpers de module pour garder ce composable sous le seuil
 * de complexité cognitive (pas de lifecycle si `autoRefresh: false`).
 */
export function useDeploymentMaintenance(options?: { autoRefresh?: boolean }) {
  const runtimeConfig = useRuntimeConfig()
  const orgStore = useOrgStore()
  const autoRefresh = options?.autoRefresh !== false

  const remoteState = useState<DeploymentMaintenanceState>('deployment-maintenance:state', () => DEFAULT_STATE)
  const devForcedState = useState<DeploymentMaintenanceState | null>(
    'deployment-maintenance:devForcedState',
    () => readDevForcedStateFromStorage(),
  )
  const refreshInFlight = ref(false)

  const graceWriteAvailable = useState<boolean>('deployment-maintenance:graceAvailable', () => false)
  const graceWriteConsumed = useState<boolean>('deployment-maintenance:graceConsumed', () => false)
  const forceExitEditMode = useState<boolean>('deployment-maintenance:forceExitEditMode', () => false)

  const hasSeenActiveThisTab = useState<boolean>('deployment-maintenance:seenActive', () => false)
  const lastStartedAtSeen = useState<string | null>('deployment-maintenance:lastStartedAtSeen', () => null)

  const shouldShowUpdatedModal = useState<boolean>('deployment-maintenance:showUpdatedModal', () => false)

  const derived = createDeploymentMaintenanceDerived(
    remoteState,
    devForcedState,
    graceWriteAvailable,
    graceWriteConsumed,
  )

  async function refresh(): Promise<void> {
    await refreshDeploymentMaintenance({
      remoteState,
      refreshInFlight,
      state: derived.state,
      earliestStartedAtMs: derived.earliestStartedAtMs,
      activeComponents: derived.activeComponents,
      graceWriteAvailable,
      graceWriteConsumed,
      hasDevForcedOverride: derived.hasDevForcedOverride,
      lastStartedAtSeen,
      hasSeenActiveThisTab,
      shouldShowUpdatedModal,
      editEnteredAtMs: orgStore.editEnteredAtMs,
      apiBaseUrl: runtimeConfig.public.apiBaseUrl as string,
    })
  }

  bindDeploymentMaintenanceLifecycle({
    autoRefresh,
    refresh,
    isActive: () => derived.state.value.active,
    onDevForcedStorage: (event) => handleDevForcedStorageEvent(event, devForcedState),
    syncDevForcedFromStorage: () => readDevForcedIntoSlot(devForcedState),
  })

  return {
    state: derived.state,
    remoteState,
    devForcedState,
    hasDevForcedOverride: derived.hasDevForcedOverride,
    setDevForcedState: (next: DeploymentMaintenanceState) => applyDevForcedState(devForcedState, next),
    clearDevForcedState: () => applyDevForcedState(devForcedState, null),
    activeComponents: derived.activeComponents,
    isWriteBlocked: derived.isWriteBlocked,
    forceExitEditMode,
    shouldShowUpdatedModal,
    dismissUpdatedModal: () => {
      shouldShowUpdatedModal.value = false
    },
    refresh,
    shouldBlockRequest: (method: string) => shouldBlockMutatingRequest(method, derived.isWriteBlocked.value),
    consumeGraceWrite: () =>
      consumeGraceWriteOnRuntime(
        graceWriteAvailable,
        graceWriteConsumed,
        derived.state.value.active,
        forceExitEditMode,
      ),
    mutationBlockedMessage: () => mutationBlockedMessageFor(derived.state.value.active),
  }
}
